import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RichTextEditorComponent } from '../../RsdForms/CreateMoment/components/RichTextEditorComponent';
import { FormField as StoreFormField } from '../../RsdForms/store/slice';
import { fetchDropdownOptions } from '../../RsdForms/store/api';
import { selectHierarchyFilters } from '../../../pages/Home/slice/selectors';

import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import ChevronDownFigma from '../Overview/icons/chevron-down-figma.svg';
import '../styles/figmaPixelPerfectOverviewField.css';
import ToolTipWrapper from 'app/components/ToolTipWrapper/ToolTipWrapper';
import { SLIDEOUT_TOOLTIP_MAPPING } from 'utils/constants';

interface DropdownOption {
  id: string;
  name: string;
}

interface FigmaPixelPerfectOverviewFieldProps {
  field: StoreFormField;
  value: { options?: string[]; option?: string; content: string };
  onChange: (name: string, value: any) => void;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  onSubmit?: () => void; // Optional submit callback
  onCancel?: () => void; // Optional cancel callback to revert parent state
  savedContent?: string; // Last saved content for cancel revert
  lastUpdated?: string;
}

export const FigmaPixelPerfectOverviewField: React.FC<
  FigmaPixelPerfectOverviewFieldProps
> = ({
  field,
  value = { options: [], option: '', content: '' },
  onChange,
  label,
  placeholder = 'Enter details here...',
  disabled = false,
  error = false,
  onSubmit,
  onCancel,
  savedContent,
  lastUpdated
}) => {
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isTextEditorOpen, setIsTextEditorOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | string[]>(
    field.type === 'multi_select_richtext'
      ? value.options || []
      : value.option || ''
  );
  const [textContent, setTextContent] = useState(value.content || '');
  const textAreaRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const htmlContentRef = useRef<HTMLDivElement>(null);
  const fieldContainerRef = useRef<HTMLDivElement>(null);

  // State for read more/less functionality
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const contentMeasureRef = useRef<HTMLDivElement>(null);

  const MAX_COLLAPSED_HEIGHT = 288;

  const isDirectlyOpenableHref = (href: string): boolean =>
    /^(https?:|mailto:|tel:|blob:|data:|about:)/i.test(href);

  // Utility function to handle link clicks in rendered HTML content
  const handleLinkClick = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      const anchor = target as HTMLAnchorElement;
      const href = anchor.getAttribute('href');

      if (href) {
        event.preventDefault();
        event.stopPropagation(); // Prevent text area click handler from firing

        if (href.startsWith('mailto:') || href.startsWith('tel:')) {
          window.location.href = href;
        } else if (isDirectlyOpenableHref(href)) {
          window.open(href, '_blank', 'noopener,noreferrer');
        } else {
          // For all other links (including domain names without protocol), add https://
          const urlToOpen =
            href.startsWith('www.') || href.includes('.')
              ? `https://${href}`
              : href;
          window.open(urlToOpen, '_blank', 'noopener,noreferrer');
        }
      }
    }
  };

  // Add event listener for link clicks in rendered HTML content
  useEffect(() => {
    const currentRef = htmlContentRef.current;
    if (currentRef) {
      currentRef.addEventListener('click', handleLinkClick, true);
      return () => {
        currentRef.removeEventListener('click', handleLinkClick, true);
      };
    }
  }, []);

  // Get customerAudiences from Redux store for "whoIsItFor" field
  const hierarchyFilters = useSelector(selectHierarchyFilters);

  // Determine if this is multi-select or single-select
  const isMultiSelect = field.type === 'multi_select_richtext';

  // Sync selectedValue with incoming value prop changes
  useEffect(() => {
    setSelectedValue(
      field.type === 'multi_select_richtext'
        ? value.options || []
        : value.option || ''
    );
  }, [value.options, value.option, field.type]);

  // Sync textContent with incoming value prop changes
  useEffect(() => {
    setTextContent(value.content || '');
  }, [value.content]);

  // Measure content height to determine if "Read more" is needed
  useEffect(() => {
    const measureHeight = () => {
      if (contentMeasureRef.current) {
        setContentHeight(contentMeasureRef.current.scrollHeight);
      }
    };

    // Measure height after content renders
    measureHeight();

    // Re-measure on window resize
    window.addEventListener('resize', measureHeight);
    return () => window.removeEventListener('resize', measureHeight);
  }, [textContent, isTextEditorOpen]);

  useEffect(() => {
    // Special handling for "whoIsItFor" field - use cached customerAudiences
    if (field.name === 'whoIsItFor' || field.id === 'whoIsItFor') {
      if (
        hierarchyFilters.customerAudiences &&
        hierarchyFilters.customerAudiences.length > 0
      ) {
        const customerAudienceOptions = hierarchyFilters.customerAudiences.map(
          (audience: string) => ({
            id: audience,
            name: audience
          })
        );
        setOptions(customerAudienceOptions);
        setIsLoadingOptions(false);
        return;
      }
    }

    // Load dropdown options from API for other fields
    if (field.api) {
      setIsLoadingOptions(true);
      fetchDropdownOptions(field.api)
        .then(response => {
          if (response && Array.isArray(response)) {
            setOptions(response);
          } else {
            setOptions([]);
          }
          setIsLoadingOptions(false);
        })
        .catch(error => {
          // Fallback to static options if available
          if (field.options) {
            setOptions(
              field.options.map((opt: any) => ({
                id: opt.value || opt.id || opt.name || opt,
                name: opt.label || opt.name || opt
              }))
            );
          } else {
            setOptions([]);
          }
          setIsLoadingOptions(false);
        });
    } else if (field.options) {
      // Use static options
      setOptions(
        field.options.map((opt: any) => ({
          id: opt.value || opt.id || opt.name || opt,
          name: opt.label || opt.name || opt
        }))
      );
      setIsLoadingOptions(false);
    } else {
      // No options configured
      setOptions([]);
      setIsLoadingOptions(false);
    }
  }, [
    field.options,
    field.api,
    field.name,
    field.id,
    hierarchyFilters.customerAudiences
  ]);

  const handleDropdownChange = (selectedKey: any) => {
    if (isMultiSelect) {
      // For multi-select, toggle the option in the array
      const currentOptions = Array.isArray(selectedValue) ? selectedValue : [];

      let newOptions;
      if (currentOptions.includes(selectedKey)) {
        // Remove the selected option
        newOptions = currentOptions.filter(opt => opt !== selectedKey);
      } else {
        // Add the selected option and remove N/A if it exists
        const optionsWithoutNA = currentOptions.filter(opt => opt !== 'N/A');
        newOptions = [...optionsWithoutNA, selectedKey];
      }

      // ✅ FIX: If all options unchecked, set to N/A
      if (newOptions.length === 0) {
        newOptions = ['N/A'];
      }

      setSelectedValue(newOptions);

      // Update the parent component
      const updatedValue = {
        ...value,
        options: newOptions
      };
      onChange(field.name, updatedValue);

      // Don't close dropdown for multi-select
    } else {
      // ✅ FIX: For single-select, allow toggling (uncheck if already selected)
      const key = selectedKey as string;
      const currentValue = Array.isArray(selectedValue)
        ? selectedValue[0]
        : selectedValue;

      let newValue;
      if (currentValue === key) {
        // If clicking the same option, uncheck it and set to N/A
        newValue = 'N/A';
      } else {
        // Otherwise, select the new option
        newValue = key;
      }

      setSelectedValue(newValue);

      // Update the parent component
      const updatedValue = {
        ...value,
        option: newValue
      };
      onChange(field.name, updatedValue);

      // Close dropdown for single-select
      setIsDropdownOpen(false);
    }
  };

  // Click outside to close dropdown (absolute menu lives inside picker container)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!isDropdownOpen) return;
      if (
        dropdownButtonRef.current?.contains(target) ||
        dropdownMenuRef.current?.contains(target)
      ) {
        return;
      }
      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleTextAreaClick = (e: React.MouseEvent) => {
    // RBAC: Prevent editing if disabled
    if (disabled) return;

    const target = e.target as HTMLElement;

    // If user clicked on a link, don't open text editor
    if (target.tagName === 'A' || target.closest('a')) {
      return; // Let the link click handler deal with it
    }

    e.stopPropagation();
    setIsTextEditorOpen(true);
  };

  const handleTextChange = (newContent: string) => {
    setTextContent(newContent);
    const updatedValue = {
      ...value,
      content: newContent
    };
    onChange(field.name, updatedValue);
  };

  const getDisplayValue = () => {
    if (isMultiSelect) {
      const selectedOptions = Array.isArray(selectedValue) ? selectedValue : [];
      return selectedOptions.length > 0
        ? selectedOptions.join(', ')
        : 'Select options...';
    } else {
      const selected = Array.isArray(selectedValue)
        ? selectedValue[0]
        : selectedValue;
      const selectedOption = options.find(
        opt => opt.id === selected || opt.name === selected
      );
      return selectedOption
        ? selectedOption.name
        : selected || 'Select option...';
    }
  };

  return (
    <div className="figma-pixel-perfect-overview-field" ref={fieldContainerRef}>
      {/* Label Section - Fixed 160px width */}
      {/* <ToolTipWrapper message={SLIDEOUT_TOOLTIP_MAPPING[label]}> */}
      <div className="figma-label-section">
        <div className="figma-label-text">{label}</div>
      </div>
      {/* </ToolTipWrapper> */}

      {/* Content Section - Flexible width */}
      <div className="figma-content-section">
        {/* Dropdown Picker */}
        <div className="figma-picker-container">
          <div
            ref={dropdownButtonRef}
            className="figma-picker-field"
            style={{
              cursor: disabled ? 'not-allowed' : 'pointer'
            }}
            onClick={() => {
              if (disabled) return; // RBAC: Prevent dropdown when disabled
              setIsDropdownOpen(!isDropdownOpen);
            }}
          >
            <div className="figma-picker-text-stack">
              <div className="figma-picker-value">{getDisplayValue()}</div>
            </div>
            <div className="figma-picker-right-container">
              <div className="figma-picker-chevron">
                <img
                  src={ChevronDownFigma}
                  alt="Chevron Down"
                  width={10}
                  height={6}
                />
              </div>
            </div>
          </div>

          {/* Absolute-positioned dropdown (same pattern as Audience / Must Win) */}
          {isDropdownOpen && !disabled && (
            <div
              ref={dropdownMenuRef}
              className="overview-dropdown-menu"
              data-testid="overview-field-dropdown-menu"
              onClick={e => e.stopPropagation()}
            >
              {isLoadingOptions ? (
                <div className="overview-dropdown-empty">
                  Loading options...
                </div>
              ) : options.length === 0 ? (
                <div className="overview-dropdown-empty">
                  No options available
                </div>
              ) : (
                options.map(item => {
                  const isSelected = isMultiSelect
                    ? Array.isArray(selectedValue) &&
                      selectedValue.includes(item.id)
                    : (Array.isArray(selectedValue)
                        ? selectedValue[0]
                        : selectedValue) === item.id;
                  return (
                    <div
                      key={item.id}
                      className="overview-dropdown-option"
                      onMouseDown={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDropdownChange(item.id);
                      }}
                    >
                      <span className="overview-dropdown-option-text">
                        {item.name}
                      </span>
                      {isSelected && (
                        <span className="overview-dropdown-checkmark">
                          <BlackCheckMarkIcon width={18} height={14} />
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Text Content Area */}
        <div
          className="figma-text-content-area"
          ref={textAreaRef}
          onClick={handleTextAreaClick}
          style={{
            cursor: disabled ? 'default' : 'pointer'
          }}
        >
          {!isTextEditorOpen ? (
            <>
              <div
                ref={contentMeasureRef}
                className={`figma-text-display ${
                  !textContent ? 'figma-text-placeholder' : ''
                } ${
                  !isExpanded && contentHeight > MAX_COLLAPSED_HEIGHT
                    ? 'figma-text-collapsed'
                    : ''
                }`}
                style={{
                  maxHeight:
                    !isExpanded && contentHeight > MAX_COLLAPSED_HEIGHT
                      ? `${MAX_COLLAPSED_HEIGHT}px`
                      : 'none',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                <div
                  ref={htmlContentRef}
                  dangerouslySetInnerHTML={{
                    __html: textContent || placeholder
                  }}
                />
              </div>
              {contentHeight > MAX_COLLAPSED_HEIGHT && (
                <button
                  className="figma-read-more-button"
                  onClick={e => {
                    e.stopPropagation();

                    if (!isExpanded) {
                      // Just expand
                      setIsExpanded(true);
                    } else {
                      // When collapsing (Read Less clicked)
                      setIsExpanded(false);

                      // After collapsing, scroll to show the field properly
                      requestAnimationFrame(() => {
                        const fieldContainer = fieldContainerRef.current;

                        if (fieldContainer) {
                          // Find the scrolling container (the slideout content container)
                          const scrollingContainer = document.querySelector(
                            '[data-testid="slideout-content-container"]'
                          ) as HTMLElement;

                          if (scrollingContainer) {
                            // Get the field's position relative to the scrolling container
                            const fieldRect =
                              fieldContainer.getBoundingClientRect();
                            const containerRect =
                              scrollingContainer.getBoundingClientRect();

                            // Calculate the position of the field within the scrolling container
                            const fieldRelativeTop =
                              fieldRect.top - containerRect.top;

                            // Check if the field is above the viewport or too far down
                            if (
                              fieldRelativeTop < 0 ||
                              fieldRelativeTop > containerRect.height - 200
                            ) {
                              // Scroll the container to put the field at a comfortable reading position
                              const targetScrollPosition =
                                scrollingContainer.scrollTop +
                                fieldRelativeTop -
                                100;

                              scrollingContainer.scrollTo({
                                top: Math.max(0, targetScrollPosition),
                                behavior: 'smooth'
                              });
                            }
                          }
                        }
                      });
                    }
                  }}
                  aria-label={isExpanded ? 'Read Less' : 'Read More'}
                >
                  {isExpanded ? 'Read Less' : 'Read More'}
                </button>
              )}
            </>
          ) : (
            <RichTextEditorComponent
              field={{
                id: field.name,
                name: field.name,
                label,
                required: false,
                type: 'richtext'
              }}
              value={textContent}
              onChange={(_, value) => handleTextChange(value)}
              externalTimestamp={lastUpdated}
              minHeight="300px"
              hideLabel
              disabled={disabled}
              footer={
                !disabled ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      style={{
                        background: 'var(--Palette-gray-200, #E6E6E6)',
                        border: 'none',
                        borderRadius: '16px',
                        height: '24px',
                        width: '57px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        padding: '0 12px',
                        fontFamily: 'Adobe Clean',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: 'var(--Alias-content-neutral-default, #222)',
                        minWidth: 'auto'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        // Revert to last SAVED content (not unsaved prop value)
                        setTextContent(savedContent || '');
                        // Notify parent to revert its local state as well
                        onCancel?.();
                        // Close the editor
                        setIsTextEditorOpen(false);
                      }}
                      aria-label="Cancel"
                    >
                      Cancel
                    </button>
                    <button
                      style={{
                        background: '#1473E6',
                        border: 'none',
                        borderRadius: '16px',
                        height: '24px',
                        width: '57px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        padding: '0 12px',
                        fontFamily: 'Adobe Clean',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        minWidth: 'auto'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        setIsTextEditorOpen(false);
                        // Trigger submit when done editing
                        onSubmit?.();
                      }}
                      aria-label="Save"
                    >
                      Save
                    </button>
                  </div>
                ) : undefined
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FigmaPixelPerfectOverviewField;
